import { Graphics, Container, Sprite, Texture, Rectangle as PIXI_Rectangle } from 'pixi.js';
import { tween, delay } from '../core/TweenManager';
import { assetLoader } from '../core/AssetLoader';

// Base asteroid color (gray for untyped asteroids)
const BASE_ASTEROID_COLOR = 0x808080;

// Resource colors - 4 ore types as per mining.md
export const RESOURCE_COLORS: Record<string, number> = {
  iron: 0x808080,       // Gray (T1)
  copper: 0xB87333,     // Copper/bronze (T2)
  titanium: 0xC0C0C0,   // Silver (T3)
  platinum: 0xFFD700,   // Gold (T4)
};

// Glow colors for each resource type (slightly brighter/shifted for glow effect)
export const RESOURCE_GLOW_COLORS: Record<string, number> = {
  iron: 0xA0A0A0,       // Light gray
  copper: 0xDD8844,     // Soft orange
  titanium: 0xE0E8FF,   // White-blue
  platinum: 0xFFE866,   // Bright gold
};

export type AsteroidSize = 'small' | 'medium' | 'large' | 'huge';

interface AsteroidDesign {
  radius: number;
  points: number;
  craters: number;
  lineWidth: number;
}

export const ASTEROID_DESIGNS: Record<AsteroidSize, AsteroidDesign> = {
  small: { radius: 80, points: 10, craters: 3, lineWidth: 2.5 },
  medium: { radius: 120, points: 12, craters: 4, lineWidth: 3 },
  large: { radius: 180, points: 14, craters: 5, lineWidth: 3.5 },
  huge: { radius: 250, points: 16, craters: 7, lineWidth: 4 },
};

// ==================== V1 HARVESTABLE GRAPHICS PRESETS ====================
// Each preset reskins the procedural asteroid look for a different field type.
// Same overall silhouette + glow technique, different palette and shape jitter
// so ice / gas / wreckage feel visually distinct without new rendering paths.

export interface GraphicsPreset {
  colorMap: Record<string, number>;
  glowMap: Record<string, number>;
  defaultColor: number;
  fillAlpha: number;
  glowAlphaMultiplier: number;
  pointJitter: number;                              // 0..1 — fraction of slice width to jitter
  radiusVariation: { min: number; max: number };    // outline irregularity
  craterAlpha: number;                              // 0 = no crater render
}

export const ASTEROID_PRESET: GraphicsPreset = {
  colorMap: RESOURCE_COLORS,
  glowMap: RESOURCE_GLOW_COLORS,
  defaultColor: BASE_ASTEROID_COLOR,
  fillAlpha: 0.4,
  glowAlphaMultiplier: 1.0,
  pointJitter: 0.2,
  radiusVariation: { min: 0.75, max: 1.0 },
  craterAlpha: 0.2,
};

export const ICE_PRESET: GraphicsPreset = {
  colorMap: {
    water_ice:  0xCCEEFF, // T1
    ammonia:    0x9BB8D3, // T2
    methane:    0x88DDFF, // T3
    helium3:    0xFFFFCC, // T4
  },
  glowMap: {
    water_ice:  0xAADDFF,
    ammonia:    0xBCD3E8,
    methane:    0xB8E8FF,
    helium3:    0xFFFFEE,
  },
  defaultColor: 0xCCEEFF,
  fillAlpha: 0.30,
  glowAlphaMultiplier: 1.3,
  pointJitter: 0.35,
  radiusVariation: { min: 0.65, max: 1.0 },
  craterAlpha: 0.10,
};

export const GAS_PRESET: GraphicsPreset = {
  colorMap: {
    hydrogen:   0xCCFFCC, // T1
    nitrogen:   0xAADDFF, // T2
    xenon:      0xBB99FF, // T3
    antimatter: 0xFF00FF, // T4
  },
  glowMap: {
    hydrogen:   0xDDFFDD,
    nitrogen:   0xC4DDFF,
    xenon:      0xCCAAFF,
    antimatter: 0xFF66FF,
  },
  defaultColor: 0xCCFFCC,
  fillAlpha: 0.45,
  glowAlphaMultiplier: 2.2,
  pointJitter: 0.15,
  radiusVariation: { min: 0.85, max: 1.0 },
  craterAlpha: 0,
};

export const WRECKAGE_PRESET: GraphicsPreset = {
  colorMap: {
    steel_alloy:   0x71797E, // T1
    cryo_bronze:   0xCD7F32, // T2
    carbide_plate: 0x36454F, // T3
    quantum_alloy: 0x00FFFF, // T4
  },
  glowMap: {
    steel_alloy:   0x9AA0A4,
    cryo_bronze:   0xE89A55,
    carbide_plate: 0x586875,
    quantum_alloy: 0x66FFFF,
  },
  defaultColor: 0x71797E,
  fillAlpha: 0.45,
  glowAlphaMultiplier: 0.5,
  pointJitter: 0.6,
  radiusVariation: { min: 0.5, max: 1.0 },
  craterAlpha: 0.30,
};

// Degrees to radians
function degToRad(deg: number): number {
  return deg * (Math.PI / 180);
}

export class AsteroidGraphics extends Container {
  private glowGraphics: Graphics;  // Glow layer (behind asteroid)
  private bodyGraphics: Graphics;  // Asteroid body
  private design: AsteroidDesign;
  private preset: GraphicsPreset;
  private asteroidColor: number;
  private glowColor: number;
  private outlinePoints: { x: number; y: number }[];
  private craterData: { x: number; y: number; r: number }[];
  private resourceType: string;

  constructor(
    x: number,
    y: number,
    size: AsteroidSize,
    resourceType: string = 'iron',
    preset: GraphicsPreset = ASTEROID_PRESET,
  ) {
    super();
    this.position.set(x, y);

    this.design = ASTEROID_DESIGNS[size];
    this.preset = preset;
    this.resourceType = resourceType;
    this.asteroidColor = preset.colorMap[resourceType] || preset.defaultColor;
    this.glowColor = preset.glowMap[resourceType] || this.asteroidColor;

    // Create graphics layers
    this.glowGraphics = new Graphics();
    this.bodyGraphics = new Graphics();
    this.addChild(this.glowGraphics);
    this.addChild(this.bodyGraphics);

    // Generate random but consistent shape (preset-tuned)
    this.outlinePoints = this.generateOutlinePoints();
    this.craterData = this.generateCraters();

    this.drawAsteroid();
  }

  private generateOutlinePoints(): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = [];
    const { radius, points: numPoints } = this.design;
    const { pointJitter, radiusVariation } = this.preset;
    const radSpan = radiusVariation.max - radiusVariation.min;

    for (let i = 0; i < numPoints; i++) {
      // Base angle with preset-controlled jitter
      const baseAngle = (i / numPoints) * Math.PI * 2;
      const angleJitter = (Math.random() - 0.5) * (Math.PI * 2 / numPoints) * pointJitter;
      const angle = baseAngle + angleJitter;

      // Radius variation per preset (wreckage = jagged, gas = round, ice = somewhere between)
      const variation = radiusVariation.min + Math.random() * radSpan;
      const r = radius * variation;
      points.push({
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r,
      });
    }

    return points;
  }

  private generateCraters(): { x: number; y: number; r: number }[] {
    const craters: { x: number; y: number; r: number }[] = [];
    const { radius, craters: numCraters } = this.design;

    for (let i = 0; i < numCraters; i++) {
      // Place craters within the asteroid bounds
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.5;
      const craterRadius = radius * (0.15 + Math.random() * 0.15);

      craters.push({
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        r: craterRadius,
      });
    }

    return craters;
  }

  private drawAsteroid(): void {
    this.glowGraphics.clear();
    this.bodyGraphics.clear();

    const { lineWidth } = this.design;
    const gMult = this.preset.glowAlphaMultiplier;

    // Build flat points array for polygon
    const flatPoints: number[] = [];
    for (const point of this.outlinePoints) {
      flatPoints.push(point.x, point.y);
    }

    // Draw glow layers (wider strokes with low alpha for soft glow effect)
    // Wide outer glow
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({ width: 40, color: this.glowColor, alpha: 0.08 * gMult });

    // Medium glow
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({ width: 20, color: this.glowColor, alpha: 0.12 * gMult });

    // Tight glow
    this.glowGraphics.poly(flatPoints, true);
    this.glowGraphics.stroke({ width: 10, color: this.glowColor, alpha: 0.18 * gMult });

    // Draw asteroid body with solid fill
    this.bodyGraphics.poly(flatPoints, true);
    this.bodyGraphics.fill({ color: this.asteroidColor, alpha: this.preset.fillAlpha });
    this.bodyGraphics.stroke({ width: lineWidth, color: this.asteroidColor, alpha: 1 });

    // Draw crater circles (inner details) - darker for depth. Skipped when craterAlpha=0.
    if (this.preset.craterAlpha > 0) {
      for (const crater of this.craterData) {
        this.bodyGraphics.circle(crater.x, crater.y, crater.r);
        this.bodyGraphics.fill({ color: 0x000000, alpha: this.preset.craterAlpha });
        this.bodyGraphics.stroke({ width: lineWidth * 0.5, color: this.asteroidColor, alpha: 0.4 });
      }
    }
  }

  /** Temporarily switch to outline-only rendering (for explosion fragments). */
  drawOutlineOnly(): void {
    this.glowGraphics.clear();
    this.bodyGraphics.clear();

    const { lineWidth } = this.design;
    const flatPoints: number[] = [];
    for (const point of this.outlinePoints) {
      flatPoints.push(point.x, point.y);
    }

    // Outline only — no fill, no glow, no craters
    this.bodyGraphics.poly(flatPoints, true);
    this.bodyGraphics.stroke({ width: lineWidth, color: this.asteroidColor, alpha: 1 });
  }

  /** Restore full rendering (fill + glow + craters). */
  restoreFullDraw(): void {
    this.drawAsteroid();
  }

  // Update color when resource type changes (for future use)
  setResourceType(resourceType: string): void {
    this.resourceType = resourceType;
    this.asteroidColor = this.preset.colorMap[resourceType] || this.preset.defaultColor;
    this.glowColor = this.preset.glowMap[resourceType] || this.asteroidColor;
    this.drawAsteroid();
  }

  // Get bounds for physics
  getRadius(): number {
    return this.design.radius;
  }

  // Get outline points for debris creation
  getOutlinePoints(): { x: number; y: number }[] {
    return [...this.outlinePoints];
  }

  // Visibility helpers
  setVisible(visible: boolean): this {
    this.visible = visible;
    return this;
  }

  setAlpha(alpha: number): this {
    this.alpha = alpha;
    return this;
  }
}

// Debris piece for asteroid destruction animation
export class AsteroidDebris extends Graphics {
  private velocityX: number;
  private velocityY: number;
  private angularVelocity: number;
  private lifetime: number = 1500;
  private startTime: number;
  private points: { x: number; y: number }[];
  private color: number;

  constructor(
    x: number,
    y: number,
    points: { x: number; y: number }[],
    color: number,
    explosionAngle: number,
    baseRotation: number
  ) {
    super();
    this.position.set(x, y);
    this.points = points;
    this.color = color;
    this.startTime = Date.now();

    // Random velocity away from center
    const speed = 80 + Math.random() * 150;
    const angle = explosionAngle + (Math.random() - 0.5) * 1.2;
    this.velocityX = Math.cos(angle) * speed;
    this.velocityY = Math.sin(angle) * speed;
    this.angularVelocity = (Math.random() - 0.5) * 300;

    this.rotation = degToRad(baseRotation);
    this.drawPiece();
  }

  private drawPiece(): void {
    this.clear();
    const flatPoints: number[] = [];
    for (const point of this.points) {
      flatPoints.push(point.x, point.y);
    }
    this.poly(flatPoints, true);
    this.stroke({ width: 1.5, color: this.color, alpha: 1 });
  }

  // Update debris - returns false when debris should be removed
  update(delta: number): boolean {
    const elapsed = Date.now() - this.startTime;
    if (elapsed > this.lifetime) {
      this.destroy();
      return false;
    }

    // Apply velocity
    const dt = delta / 1000;
    this.x += this.velocityX * dt;
    this.y += this.velocityY * dt;
    this.rotation += degToRad(this.angularVelocity * dt);

    // Fade out
    this.alpha = 1 - (elapsed / this.lifetime);

    // Slow down
    this.velocityX *= 0.98;
    this.velocityY *= 0.98;

    return true;
  }
}

// Create asteroid explosion with debris
export function createAsteroidExplosion(
  parent: Container,
  x: number,
  y: number,
  rotation: number,
  size: AsteroidSize,
  resourceType: string
): AsteroidDebris[] {
  const design = ASTEROID_DESIGNS[size];
  const color = RESOURCE_COLORS[resourceType] || BASE_ASTEROID_COLOR;
  const debris: AsteroidDebris[] = [];

  // Generate random outline for this explosion (matching the irregular shape)
  const outlinePoints: { x: number; y: number }[] = [];
  const numPoints = design.points;
  const radius = design.radius;

  for (let i = 0; i < numPoints; i++) {
    // Base angle with minimal jitter for smoother, rounder shape
    const baseAngle = (i / numPoints) * Math.PI * 2;
    const angleJitter = (Math.random() - 0.5) * (Math.PI * 2 / numPoints) * 0.2;
    const angle = baseAngle + angleJitter;

    // Less radius variation for rounder, less pointy asteroids (75%-100%)
    const radiusVariation = 0.75 + Math.random() * 0.25;
    const r = radius * radiusVariation;
    outlinePoints.push({
      x: Math.cos(angle) * r,
      y: Math.sin(angle) * r,
    });
  }

  // Split asteroid into triangular debris pieces
  const centerX = 0;
  const centerY = 0;

  for (let i = 0; i < outlinePoints.length; i++) {
    const p1 = outlinePoints[i];
    const p2 = outlinePoints[(i + 1) % outlinePoints.length];

    // Create triangular debris from center to edge
    const piecePoints = [
      { x: centerX, y: centerY },
      { x: p1.x, y: p1.y },
      { x: p2.x, y: p2.y },
    ];

    // Calculate piece center
    const pieceCenterX = (centerX + p1.x + p2.x) / 3;
    const pieceCenterY = (centerY + p1.y + p2.y) / 3;

    // Rotate center point by asteroid rotation
    const rotRad = degToRad(rotation);
    const rotatedX = pieceCenterX * Math.cos(rotRad) - pieceCenterY * Math.sin(rotRad);
    const rotatedY = pieceCenterX * Math.sin(rotRad) + pieceCenterY * Math.cos(rotRad);

    // Explosion angle from center
    const explosionAngle = Math.atan2(rotatedY, rotatedX);

    const debrisPiece = new AsteroidDebris(
      x + rotatedX,
      y + rotatedY,
      piecePoints,
      color,
      explosionAngle,
      rotation
    );
    parent.addChild(debrisPiece);
    debris.push(debrisPiece);
  }

  // Add particle explosion effect (simplified - no particle system)
  // Create small spark graphics instead
  const sparkColors = [color, 0x20a5a6, 0xf5e8d1];
  for (let i = 0; i < 15; i++) {
    const spark = new Graphics();
    spark.circle(0, 0, 2 + Math.random() * 3);
    const sparkColor = sparkColors[Math.floor(Math.random() * sparkColors.length)];
    spark.fill({ color: sparkColor, alpha: 1 });
    spark.position.set(x, y);
    spark.zIndex = 10;
    parent.addChild(spark);

    const angle = Math.random() * Math.PI * 2;
    const speed = 80 + Math.random() * 120;
    const targetX = x + Math.cos(angle) * speed * 0.4;
    const targetY = y + Math.sin(angle) * speed * 0.4;

    tween({
      targets: spark,
      x: targetX,
      y: targetY,
      alpha: 0,
      duration: 300 + Math.random() * 100,
      onComplete: () => spark.destroy(),
    });
  }

  return debris;
}

/**
 * Texture-based explosion: snapshots the visual container into a texture,
 * splits it into a grid of fragments that drift outward, rotate, and fade.
 * Same technique as createSVGExplosion for ships, generalized for any entity.
 *
 * @param parent       - Scene container to add fragments to
 * @param visual       - The Container to snapshot (AsteroidGraphics, SVG sprite, etc.)
 * @param worldX       - World X position of the entity center
 * @param worldY       - World Y position of the entity center
 * @param entityRotation - Current rotation of the entity (radians)
 * @param color        - Tint color for the flash effect
 * @param radius       - Entity radius (used for flash size)
 */
export function createTextureExplosion(
  parent: Container,
  visual: Container,
  worldX: number,
  worldY: number,
  entityRotation: number,
  color: number,
  radius: number,
): void {
  // Snapshot the visual into a texture
  // Reset visual transform so generateTexture captures it cleanly
  const savedX = visual.x;
  const savedY = visual.y;
  const savedRot = visual.rotation;
  const savedAlpha = visual.alpha;
  visual.x = 0;
  visual.y = 0;
  visual.rotation = 0;
  visual.alpha = 1;

  let texture: Texture;
  try {
    texture = assetLoader.generateTexture(visual, `_explosion_${Date.now()}`, 0.5);
  } catch {
    // Renderer not ready — fall back silently
    visual.x = savedX;
    visual.y = savedY;
    visual.rotation = savedRot;
    visual.alpha = savedAlpha;
    return;
  }

  // Restore (entity is about to be destroyed anyway, but be safe)
  visual.x = savedX;
  visual.y = savedY;
  visual.rotation = savedRot;
  visual.alpha = savedAlpha;

  const texW = texture.width;
  const texH = texture.height;
  // 0.5 resolution was used → scale fragments back up by 2
  const fragScale = 2;

  // Grid size — more pieces for bigger entities
  const cols = texW > 120 ? 4 : 3;
  const rows = texH > 120 ? 4 : 3;
  const cellW = texW / cols;
  const cellH = texH / rows;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const frame = new PIXI_Rectangle(c * cellW, r * cellH, cellW, cellH);
      const fragTexture = new Texture({ source: texture.source, frame });

      const sprite = new Sprite(fragTexture);
      sprite.anchor.set(0.5);
      sprite.scale.set(fragScale);

      // Position of this fragment in local space (centered)
      const localX = (c + 0.5 - cols / 2) * cellW * fragScale;
      const localY = (r + 0.5 - rows / 2) * cellH * fragScale;

      // Rotate local position by entity heading
      const cos = Math.cos(entityRotation);
      const sin = Math.sin(entityRotation);
      const worldLocalX = localX * cos - localY * sin;
      const worldLocalY = localX * sin + localY * cos;

      sprite.position.set(worldX + worldLocalX, worldY + worldLocalY);
      sprite.rotation = entityRotation;
      sprite.zIndex = 80;
      parent.addChild(sprite);

      // Drift direction: outward from center
      const driftAngle = Math.atan2(worldLocalY, worldLocalX) + (Math.random() - 0.5) * 0.6;
      const driftSpeed = 25 + Math.random() * 55;
      const angularSpeed = (Math.random() - 0.5) * 2;

      const startTime = Date.now();
      const lifetime = 2000 + Math.random() * 1500;
      const vx = Math.cos(driftAngle) * driftSpeed;
      const vy = Math.sin(driftAngle) * driftSpeed;

      const tick = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed > lifetime || sprite.destroyed) {
          if (!sprite.destroyed) sprite.destroy();
          fragTexture.destroy();
          return;
        }
        const dt = 1 / 60;
        sprite.x += vx * dt;
        sprite.y += vy * dt;
        sprite.rotation += angularSpeed * dt;
        sprite.alpha = 1 - (elapsed / lifetime);
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }

  // Flash at center
  const flash = new Graphics();
  flash.position.set(worldX, worldY);
  flash.circle(0, 0, radius * 0.4);
  flash.fill({ color: 0xffffff, alpha: 0.6 });
  flash.blendMode = 'add';
  flash.zIndex = 81;
  parent.addChild(flash);
  tween({
    targets: flash,
    alpha: 0,
    duration: 350,
    onComplete: () => flash.destroy(),
  });

  // Spark particles
  const sparkColors = [color, 0xffffff, 0xffaa44];
  for (let i = 0; i < 10; i++) {
    const spark = new Graphics();
    spark.circle(0, 0, 1.5 + Math.random() * 2.5);
    spark.fill({ color: sparkColors[Math.floor(Math.random() * sparkColors.length)], alpha: 1 });
    spark.position.set(worldX, worldY);
    spark.zIndex = 82;
    parent.addChild(spark);

    const angle = Math.random() * Math.PI * 2;
    const speed = 60 + Math.random() * 100;
    tween({
      targets: spark,
      x: worldX + Math.cos(angle) * speed * 0.35,
      y: worldY + Math.sin(angle) * speed * 0.35,
      alpha: 0,
      duration: 250 + Math.random() * 150,
      onComplete: () => spark.destroy(),
    });
  }

  // Clean up the snapshot texture after all fragments are done
  delay(5000, () => {
    if (!texture.destroyed) texture.destroy(true);
  });
}
